import { Controller } from "@hotwired/stimulus"

/**
 * Ui::Calendar::NowLineComponent companion controller.
 *
 * Re-positions the "now" line on day/week grids every 60 seconds.
 *
 * The element this is attached to is the now-line itself. Its `top`
 * (in pixels) is computed from the current time using `data-hour-height-value`
 * (CSS px per hour — defaults to 48, matching the existing change_calendar grid).
 *
 * Example markup (rendered by T2 NowLineComponent):
 *
 *   <div data-controller="ui--calendar--now-line-tick"
 *        data-ui--calendar--now-line-tick-hour-height-value="48"
 *        class="absolute left-0 right-0 h-px bg-red-500"></div>
 *
 * The element's own `style.top` is updated; nothing else is touched.
 */
export default class extends Controller {
  static values = {
    hourHeight: { type: Number, default: 48 },
    intervalMs: { type: Number, default: 60_000 },
  }

  connect() {
    this._reposition()
    this._interval = setInterval(() => this._reposition(), this.intervalMsValue)
  }

  disconnect() {
    if (this._interval) {
      clearInterval(this._interval)
      this._interval = null
    }
  }

  _reposition() {
    const now = new Date()
    const offset = computeNowTop(now, this.hourHeightValue)
    this.element.style.top = `${offset}px`
  }
}

/**
 * Pure helper — px offset from grid top for a given Date.
 * Exported for unit tests so we don't have to mock controller wiring.
 */
export function computeNowTop(date, hourHeightPx) {
  const hours = date.getHours()
  const minutes = date.getMinutes()
  return hours * hourHeightPx + (minutes * hourHeightPx) / 60
}
